import { default as PolyfilledSet } from "core-js/actual/set";

export type Comparable = number | bigint | string | boolean;

/**
 * Filter messages to those within a range of values.
 */
export interface Range<T> {
  min?: T;
  max?: T;
}

export type MessageFilterType = "range" | "list" | "combined";

export abstract class MessageFilter<T extends Comparable = Comparable> {
  public readonly type: MessageFilterType;

  static combine<T extends Comparable>(
    filters: MessageFilter<T>[],
  ): CombinedMessageFilter<T> {
    if (filters.length === 0) {
      throw new Error("Cannot combine an empty array of filters");
    }
    return new CombinedMessageFilter(filters);
  }

  static fromList<T extends Comparable>(values: readonly T[]): ListFilter<T> {
    return new ListFilter(values);
  }

  static fromRange<T extends Comparable>(range: Range<T>): RangeFilter<T> {
    return new RangeFilter(range);
  }

  protected constructor(type: MessageFilterType) {
    this.type = type;
  }

  abstract exceedsFilter(value: T): boolean;

  abstract matches(value: T): boolean;

  public isCombinedFilter(): this is CombinedMessageFilter<T> {
    return this.type === "combined";
  }

  public isRangeFilter(): this is RangeFilter<T> {
    return this.type === "range";
  }

  public isListFilter(): this is ListFilter<T> {
    return this.type === "list";
  }

  abstract toString(): string;
}

export class RangeFilter<T extends Comparable> extends MessageFilter<T> {
  private range: Range<T>;

  constructor(range: Range<T>) {
    super("range");
    this.range = range;
  }

  public get min(): T | undefined {
    return this.range.min;
  }

  public get max(): T | undefined {
    return this.range.max;
  }

  public exceedsFilter(value: T): boolean {
    if (this.max === undefined) {
      return false;
    }
    return value > this.max;
  }

  public matches(value: T): boolean {
    const start = this.min ?? value;
    const end = this.max ?? value;
    return value >= start && value <= end;
  }

  public toString(): string {
    return `RangeFilter({ min: ${this.min}, max: ${this.max} })`;
  }
}

export class ListFilter<T extends Comparable> extends MessageFilter<T> {
  private _matching: PolyfilledSet<T>;

  constructor(matching: readonly T[]) {
    super("list");
    this._matching = new PolyfilledSet(matching);
  }

  public get matching(): T[] {
    // Sorted by `<` and `>`. The comparator is needed because `sort()` on its
    // own compares values as strings, which puts 10n before 5n.
    return Array.from(this._matching).sort((a, b) => {
      if (a < b) {
        return -1;
      }
      if (a > b) {
        return 1;
      }
      return 0;
    });
  }

  public exceedsFilter(value: T): boolean {
    const max = this.matching.at(-1);
    if (max === undefined) {
      return false;
    }
    return value > max;
  }

  public matches(value: T | Set<T>): boolean {
    if (value instanceof Set) {
      // Matches if there are any elements in common
      return !this._matching.isDisjointFrom(value);
    }
    return this._matching.has(value);
  }

  public toString(): string {
    return `ListFilter(${this._matching.size} members)`;
  }
}

export class CombinedMessageFilter<
  T extends Comparable,
> extends MessageFilter<T> {
  public readonly filters: MessageFilter<T>[];

  constructor(filters: MessageFilter<T>[]) {
    super("combined");
    this.filters = filters;
  }

  public containsFilter(type: MessageFilterType): boolean {
    return this.filters.some((filter) => filter.type === type);
  }

  public exceedsFilter(value: T): boolean {
    return this.filters.every((filter) => filter.exceedsFilter(value));
  }

  public matches(value: T): boolean {
    return this.filters.some((filter) => filter.matches(value));
  }

  public toString(): string {
    const composed = this.filters.map((f) => f.toString());
    return `CombinedMessageFilter(${composed.join(", ")})`;
  }
}
